import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, Loader2, ShieldCheck } from "lucide-react";
import { BAIN_PRECAUTIONS } from "@/content/bainPrecautions";
import "./BreakfastSurvey.css";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * BainSurvey — page publique sécurisée par token (/bain-fleuri/:token).
 * Affiche les précautions du bain fleuri (RÉUTILISÉES du guide voyageur, aucune
 * nouvelle précaution inventée) + une confirmation « j'ai pris connaissance ».
 * La confirmation se rattache automatiquement à la bonne réservation.
 */
export default function BainSurvey() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/rg/public/bain/${token}`);
        if (!r.ok) { setNotFound(true); return; }
        setCtx(await r.json());
      } catch { setNotFound(true); }
      finally { setLoading(false); }
    })();
  }, [token]);

  const confirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    setServerError("");
    try {
      const r = await fetch(`${API}/api/rg/public/bain/${token}`, { method: "POST" });
      if (!r.ok) {
        let msg = "Une erreur est survenue, merci de réessayer.";
        try { msg = (await r.json()).detail || msg; } catch { /* keep */ }
        setServerError(msg); return;
      }
      setDone(true);
    } catch { setServerError("Connexion impossible. Vérifiez votre réseau et réessayez."); }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="bk-wrap"><div className="bk-card bk-center" data-testid="bain-loading">
      <Loader2 size={28} className="bk-spin" /><p className="bk-sub" style={{ marginTop: 12 }}>Chargement…</p>
    </div></div>
  );
  if (notFound) return (
    <div className="bk-wrap"><div className="bk-card bk-center" data-testid="bain-notfound">
      <p className="bk-title">Lien invalide</p>
      <p className="bk-sub">Ce lien n&apos;est plus valide. Merci de contacter votre hôte.</p>
    </div></div>
  );
  if (done || ctx.already_received) return (
    <div className="bk-wrap"><div className="bk-card bk-center" data-testid="bain-done">
      <span className="bk-done-icon"><Check size={30} /></span>
      <p className="bk-title">Merci {ctx.first_name || ""} ✨</p>
      <p className="bk-sub">Nous avons bien noté que vous avez pris connaissance des précautions.<br />Votre bain de fleurs sera préparé avec soin 🌸</p>
    </div></div>
  );

  const P = BAIN_PRECAUTIONS;

  return (
    <div className="bk-wrap">
      <div className="bk-card" data-testid="bain-form">
        <p className="bk-eyebrow">Curieux Refuge</p>
        <h1 className="bk-title">Votre bain fleuri 🌸</h1>
        <p className="bk-sub">{P.intro}</p>

        {serverError && <div className="bk-error" data-testid="bain-error">{serverError}</div>}

        <div className="bk-q">
          <p className="bk-eyebrow" style={{ marginTop: 8 }}>{P.eyebrow}</p>
          <h2 className="bk-voy-title" style={{ fontSize: 16 }}>{P.title}</h2>

          <label className="bk-q-label" style={{ marginTop: 10 }}>Évitez le bain en cas :</label>
          <ul className="bain-list" data-testid="bain-avoid">
            {P.avoid.map((a, i) => <li key={i}>{a}</li>)}
          </ul>

          <label className="bk-q-label" style={{ marginTop: 10 }}>Nous vous conseillons également :</label>
          <ul className="bain-list" data-testid="bain-tips">
            {P.tips.map((t, i) => <li key={i}>{t}</li>)}
          </ul>

          <p className="bk-q-hint" style={{ marginTop: 10 }}>{P.footer}</p>
        </div>

        <button type="button" className="bk-submit" disabled={submitting} onClick={confirm} data-testid="bain-confirm">
          {submitting ? <><Loader2 size={18} className="bk-spin" /> Envoi…</> : <><ShieldCheck size={18} /> J&apos;ai pris connaissance des précautions</>}
        </button>
      </div>
    </div>
  );
}
